import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Text, Button, Link, Alert } from "@oysterhr/ui"

import { PlaidLinkFlow } from "../../../../plaid/PlaidLinkFlow"
import { useMemo, useState } from "react"
import { PaymentEngagementType } from "../../../../../enums"
import {
  companyAchFlowPath,
  companyNewAchFlowPath,
  companyPaymentMethodsPath,
} from "../../../../../../../utils/routeHelpers"
import { SkipPlaidVerificationModal } from "./../SkipPlaidVerificationModal"
import { useCompanyPaymentsSettingsContext } from "../../../../../contexts/CompanyPaymentsSettingsContext"
import { HC_ARTICLE } from "../../constants"

const directionsForPlaid = [
  "directionsForPlaid.step1",
  "commonSteps.authorizationStep",
  "commonSteps.shareLinkWithBankStep",
]

const directionsForManuallySettingUpAccount = [
  "directionsForManuallySettingUpAccount.step1",
  "directionsForManuallySettingUpAccount.step2",
  "directionsForManuallySettingUpAccount.step3",
  "commonSteps.authorizationStep",
  "commonSteps.shareLinkWithBankStep",
]

export const AchContractorSetupSteps = ({
  type,
}: {
  type: PaymentEngagementType.Combined | PaymentEngagementType.Contractor
}) => {
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const { contractorDirectDebitEnabled } = useCompanyPaymentsSettingsContext()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/AchLandingPage")

  const [mode, setMode] = useState<"plaid" | "manual">("plaid")
  const [showSkipPlaidVerificationModal, setShowSkipPlaidVerificationModal] = useState(false)

  const resolvedType = useMemo(() => {
    if (type === PaymentEngagementType.Combined && !contractorDirectDebitEnabled) {
      return PaymentEngagementType.Fte
    }
    return type
  }, [type, contractorDirectDebitEnabled])

  const handleManuallyConnect = () => {
    const url =
      type === PaymentEngagementType.Combined
        ? companyAchFlowPath(companyId)
        : companyNewAchFlowPath(companyId, type)

    navigate(url)
  }

  return (
    <>
      {mode === "plaid" && (
        <>
          <div className="flex flex-col gap-6">
            <div>
              <Text className="text-base font-semibold leading-6">
                {t("whatDoesAchDo.question")}
              </Text>
              <Text size="medium" className="mt-2 text-base font-normal leading-6">
                {t("whatDoesAchDo.answer")}
              </Text>
            </div>
            <div>
              <Text className="text-base font-semibold leading-6">
                {t("whichInvoicesWillBePaid.question")}
              </Text>
              <Text size="medium" className="mt-2 text-base font-normal leading-6">
                {t(`whichInvoicesWillBePaid.answer.${resolvedType}`)}
              </Text>
            </div>
          </div>
          <Alert variation="info" className="mt-2">
            <Alert.Icon />
            <Alert.Content>
              <Text size="medium" className="text-base font-normal leading-6">
                {t("alert")}
              </Text>
            </Alert.Content>
          </Alert>
        </>
      )}

      {mode === "plaid" ? (
        <div className="mb-6">
          <Text className="mb-4 mt-6 text-base font-semibold leading-6">
            {t("connectBankToStart")}
          </Text>
          <ol className="mt-2 list-decimal rounded-lg border-2 border-gray-100 p-4">
            {directionsForPlaid.map(direction => (
              <li className="ml-5" key={direction}>
                <Text as="span">{t(direction)}</Text>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex justify-end gap-3">
            <Button variation="outline" onClick={() => setShowSkipPlaidVerificationModal(true)}>
              {t("actions.cannotVerifiyWithPlaid")}
            </Button>
            <PlaidLinkFlow
              companyId={companyId}
              onSubmit={() => navigate(companyPaymentMethodsPath(companyId))}
            />
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <Text className="mb-4 text-base font-semibold leading-6">{t("connectBankManually")}</Text>
          <ol className="mt-2 list-decimal rounded-lg border-2 border-gray-100 p-4">
            {directionsForManuallySettingUpAccount.map(direction => (
              <li className="ml-5" key={direction}>
                <Text as="span">{t(direction)}</Text>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex justify-end">
            <Button
              data-analytics-name="company_payment_methods-ach-manually-connect-account"
              variation="primary"
              onClick={handleManuallyConnect}
            >
              {t("actions.connectManually")}
            </Button>
          </div>
        </div>
      )}
      <div>
        <Text size="small" className="text-sm font-semibold leading-5 text-gray-400">
          {t("note")}
        </Text>
        <Text size="small" className="text-sm font-normal leading-5 text-gray-400">
          {t("noteDescription")}
        </Text>
      </div>
      <div className="mt-6">
        <Trans
          t={t}
          i18nKey="details"
          components={{
            a: <Link to={HC_ARTICLE} className="inline-block font-normal" target="_blank" />,
          }}
        />
      </div>

      <SkipPlaidVerificationModal
        isOpen={showSkipPlaidVerificationModal}
        onClose={() => setShowSkipPlaidVerificationModal(false)}
        onFinish={() => {
          setShowSkipPlaidVerificationModal(false)
          setMode("manual")
        }}
      />
    </>
  )
}
